import { useEffect, useState, useCallback } from "react";
import {
    Table,
    Paper,
    Stack,
    Text,
    Title,
    Loader,
    Badge,
    Group,
} from "@mantine/core";
import useTransientStore from "../../../data/transcientStore";
import { getHistory } from "../../../services/CityObjectActionHistoryService";
import { CityObjectActionHistoryType } from "../../../types/CityObjectActionHistory";
import { useTranslation } from "../../../hooks/useTranslation";

interface ActionHistoryTabProps {
    form: any;
    addressSystemId?: number;
    userId?: number;
}

export default function ActionHistoryTab({
    form,
    addressSystemId,
    userId: propUserId,
}: Readonly<ActionHistoryTabProps>) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const currentUserId = useTransientStore((state) => state.currentUserId);
    const { getTranslation } = useTranslation({
        pageId: 308, // Object tabs page
        addressSystemId: addressSystemId || selectedAddressSystem.id,
    });

    const objectId = Number(form?.values?.objectId || form?.values?.id || 0);
    const userId = propUserId || currentUserId || 0;
    const resolvedAddressSystemId = addressSystemId || selectedAddressSystem.id;

    const [data, setData] = useState<CityObjectActionHistoryType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const actionHistoryTitle =
        getTranslation("CITY_OBJECT_ACTION_HISTORY_TITLE") || "Action History";

    const loadHistory = useCallback(async () => {
        if (!objectId || !userId || resolvedAddressSystemId <= 0) return;

        setLoading(true);
        setError(null);
        try {
            const result = await getHistory(objectId, userId, resolvedAddressSystemId);
            setData(result);
        } catch (err) {
            console.error("Failed to load action history:", err);
            setError(
                getTranslation("ERROR_LOADING_HISTORY") ||
                "Failed to load action history. Please check that you have access to this object."
            );
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [objectId, userId, resolvedAddressSystemId, getTranslation]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const formatDateTime = (dateStr: string) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleString("fi-FI", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return dateStr;
        }
    };

    if (loading) {
        return (
            <Stack gap="md">
                <Title order={4}>{actionHistoryTitle}</Title>
                <Group justify="center" py="xl">
                    <Loader />
                </Group>
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack gap="md">
                <Title order={4}>{actionHistoryTitle}</Title>
                <Paper withBorder p="md" bg="red.1">
                    <Text c="red.8" size="sm">
                        {error}
                    </Text>
                </Paper>
            </Stack>
        );
    }

    if (data.length === 0) {
        return (
            <Stack gap="md">
                <Title order={4}>{actionHistoryTitle}</Title>
                <Paper withBorder p="xl" bg="gray.0">
                    <Text c="dimmed" ta="center">
                        {getTranslation("NO_HISTORY_FOUND") ||
                            "No action history available for this object."}
                    </Text>
                </Paper>
            </Stack>
        );
    }

    return (
        <Stack gap="md">
            <Title order={4}>
                {getTranslation("CITY_OBJECT_ACTION_HISTORY_TITLE") || "Action History"}
            </Title>
            <Paper withBorder p="sm" bg="gray.0">
                <Group gap="md">
                    <Text fw={600} size="sm">
                        {getTranslation("TEXT_LABEL_CONTRACT") || "Contract"}:
                    </Text>
                    <Text size="sm">{data[0]?.objectName || "—"}</Text>
                    {data[0]?.objectLocation && (
                        <Text size="sm">{data[0].objectLocation}</Text>
                    )}
                    <Badge color="blue" variant="light">
                        {data.length} {getTranslation("TEXT_LABEL_ENTRIES") || "entries"}
                    </Badge>
                </Group>
            </Paper>

            <Paper withBorder radius="md" style={{ overflow: "auto" }}>
                <Table striped highlightOnHover style={{ tableLayout: "fixed" }}>
                    <Table.Thead style={{ position: "sticky", top: 0, zIndex: 4 }}>
                        <Table.Tr bg="var(--mantine-color-gray-3)">
                            <Table.Th
                                w={150}
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    background: "var(--mantine-color-gray-3)",
                                    color: "var(--mantine-color-dark-8)",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.03em",
                                }}
                            >
                                {getTranslation("TEXT_LABEL_PVM") || "Date"}
                            </Table.Th>
                            <Table.Th
                                w={150}
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    background: "var(--mantine-color-gray-3)",
                                    color: "var(--mantine-color-dark-8)",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.03em",
                                }}
                            >
                                {getTranslation("TEXT_LABEL_NAME") || "Name"}
                            </Table.Th>
                            <Table.Th
                                w={200}
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    background: "var(--mantine-color-gray-3)",
                                    color: "var(--mantine-color-dark-8)",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.03em",
                                }}
                            >
                                {getTranslation("TEXT_LABEL_FUNCTION") || "Function"}
                            </Table.Th>
                            <Table.Th
                                w={150}
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    background: "var(--mantine-color-gray-3)",
                                    color: "var(--mantine-color-dark-8)",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.03em",
                                }}
                            >
                                {getTranslation("TEXT_LABEL_TARGET") || "Target"}
                            </Table.Th>
                            <Table.Th
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    background: "var(--mantine-color-gray-3)",
                                    color: "var(--mantine-color-dark-8)",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.03em",
                                }}
                            >
                                {getTranslation("TEXT_LABEL_DESCRIPTION") || "Description"}
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {data.map((item) => (
                            <Table.Tr key={item.id}>
                                <Table.Td>
                                    <Text size="sm">{formatDateTime(item.actionTime)}</Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm">{item.userName}</Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm">
                                        {getTranslation(item.actionType) || item.actionType}
                                    </Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text size="sm">{item.changedItem || "—"}</Text>
                                </Table.Td>
                                <Table.Td>
                                    <Text
                                        size="sm"
                                        style={{ whiteSpace: "pre-wrap" }}
                                        dangerouslySetInnerHTML={{
                                            __html: item.changeDescription || "—",
                                        }}
                                    />
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>
        </Stack>
    );
}
